import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faSearch,
  faSortUp,
  faSortDown,
  faTrash,
  faCopy,
  faFileExport,
} from "@fortawesome/free-solid-svg-icons";
import InjDispWellInfoSection from "./InjDispWellInfoSection";

// Freshdesk #1244 — whitelisted, safe lease fields for the Mass Change tool.
// Must stay in sync with the $mcAllowed whitelist in api/clientDetails.php.
const MASS_CHANGE_FIELDS = [
  { key: "GroupID", label: "Group ID", input: "text" },
  { key: "PumperID", label: "Pumper", input: "pumper" },
  { key: "ReliefID", label: "Relief Pumper", input: "pumper" },
  { key: "PumperRoute", label: "Route", input: "text" },
  { key: "District", label: "District", input: "text" },
  { key: "ReportOrder", label: "Report Order", input: "text" },
  { key: "Active", label: "Active Status", input: "active" },
];

// Encoded sentinels for the data-driven "before value" dropdown.
const MC_BEFORE_ANY = "";
const MC_BEFORE_NULL = "__NULL__";
const MC_BEFORE_BLANK = "__BLANK__";
const MC_BEFORE_VALUE_PREFIX = "val:";

function LeasesTab({
  leases,
  setLeases,
  users,
  purchasers, // Passed in from props
  tags, // Passed in from props
  theme,
  companyName,
  subdomain,
}) {
  const [showLeaseModal, setShowLeaseModal] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");
  const [isEditMode, setIsEditMode] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: "" });

  // For searching and sorting (leases grid)
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("LeaseID");
  const [sortDirection, setSortDirection] = useState("asc");

  // Show/Hide inactive leases (default Hide)
  const [showInactive, setShowInactive] = useState(false);

  // Leases Mass Change modal (Freshdesk #1244 — supersedes "Replace Pumper")
  const [showMassChangeModal, setShowMassChangeModal] = useState(false);
  const [mcField, setMcField] = useState("");
  // Data-driven "before value": options come from the leaseFieldValues API.
  const [mcBeforeSel, setMcBeforeSel] = useState(MC_BEFORE_ANY);
  const [mcBeforeOptions, setMcBeforeOptions] = useState(null); // {values,hasNull,hasBlank}
  const [mcBeforeLoading, setMcBeforeLoading] = useState(false);
  const [mcAfterIsNull, setMcAfterIsNull] = useState(false);
  const [mcAfterValue, setMcAfterValue] = useState("");
  const [mcFilterStatus, setMcFilterStatus] = useState("");
  const [mcFilterPumper, setMcFilterPumper] = useState("");
  const [mcFilterRelief, setMcFilterRelief] = useState("");
  const [mcFilterGroup, setMcFilterGroup] = useState("");
  const [mcFilterDistrict, setMcFilterDistrict] = useState("");
  const [mcFilterRoute, setMcFilterRoute] = useState("");
  const [mcFilterNameSearch, setMcFilterNameSearch] = useState("");
  const [mcPreview, setMcPreview] = useState(null);
  const [mcResult, setMcResult] = useState(null);
  const [mcBusy, setMcBusy] = useState(false);

  // Valid pumpers
  const [validPumpers, setValidPumpers] = useState([]);

  // Wells grid state (in modal)
  const [wellSearch, setWellSearch] = useState("");
  const [wellSortBy, setWellSortBy] = useState("WellID");
  const [wellSortDirection, setWellSortDirection] = useState("asc");
  const [showInactiveWells, setShowInactiveWells] = useState(false);

  // Tanks grid state (in modal)
  const [tankSearch, setTankSearch] = useState("");
  const [tankSortBy, setTankSortBy] = useState("TankID");
  const [tankSortDirection, setTankSortDirection] = useState("asc");
  const [showInactiveTanks, setShowInactiveTanks] = useState(false);

  const isDark = theme === "dark";

  // -------------------------------
  // HELPER STYLES
  // -------------------------------
  const getInputClasses = (isDarkMode) =>
    isDarkMode
      ? "w-full px-2 py-1 bg-gray-700 text-gray-100 border border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      : "w-full px-2 py-1 bg-white text-gray-800 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500";

  const getHeaderClasses = () =>
    `border-b ${
      isDark
        ? "bg-gray-700 text-gray-100 border-gray-600"
        : "bg-gray-100 text-gray-800 border-gray-200"
    }`;

  const getRowClasses = () =>
    `border-b ${
      isDark ? "border-gray-600 hover:bg-gray-600" : "hover:bg-gray-50"
    }`;

  // Helper to compute valid pumpers from full users list
  const computeValidPumpers = (allUsers = []) =>
    allUsers.filter((u) => {
      if (!u) return false;
      if (u.Disabled === "1") return false;

      const role = (u.Role || "").toUpperCase();
      // Exclude Investor and Read-only roles
      if (role === "I" || role === "R") return false;

      if (!u.perms) return false;

      let permsObj;
      try {
        permsObj = JSON.parse(u.perms);
      } catch (e) {
        return false;
      }

      const ogpumperRead = permsObj?.ogpumper?.read === true;
      const ogfieldticketRead = permsObj?.ogfieldticket?.read === true;

      // Must have read perms in at least one of these apps
      return ogpumperRead || ogfieldticketRead;
    });

  // Keep validPumpers in sync with users prop
  useEffect(() => {
    setValidPumpers(computeValidPumpers(users || []));
  }, [users]);

  // Reconcile active tab when WellType changes (INJ hides Tanks & Purchasers, shows Injection)
  useEffect(() => {
    if (!editingLease) return;
    const isInjection = (editingLease.WellType || "").toUpperCase() === "INJ";
    if (isInjection) {
      if (activeTab === "tanks" || activeTab === "purchasers") {
        setActiveTab("injection");
      }
    } else {
      if (activeTab === "injection") setActiveTab("basic");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingLease?.WellType]);

  // Blank lease object for "Add New"
  const blankLease = {
    LeaseID: "",
    LeaseName: "",
    GroupID: "",
    FieldName: "",
    County: "",
    PumperID: "",
    PumperRoute: "",
    ReliefID: "",
    District: "",
    RRC: "",
    WellType: "OIL",
    ShowOil: "Y",
    ShowWater: "Y",
    ShowGas: "Y",
    WaterMeter: "N",
    Active: "Y",
    ExcludeFromPR: "N",

    // Purchasers
    Purchaser: "",
    PurchaserLeaseNo: "",
    WaterTransport: "",

    // Injection
    UIC: "",
    Target: 0, // Daily Target (int)
    MaxInj: 0, // int
    MaxPressure: 0, // int

    PropertyNum: "",
    CP: "",
    Tag1: "",
    Tag2: "",
    Tag3: "",
    Tag4: "",
    Investor1: "",
    Investor2: "",
    wells: [],
    tanks: [],
  };

  // -------------------------------
  // CREATE/EDIT/COPY LEASE
  // -------------------------------
  const handleCreateLease = () => {
    setEditingLease({ ...blankLease });
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  const handleExportLeases = () => {
    try {
      if (!leases?.length) throw new Error("No leases to export");

      /* ───────────── helper ───────────── */
      const serialise = (val) => {
        if (Array.isArray(val)) {
          return val
            .map((el) => {
              if (el == null) return "";
              if (typeof el === "object")
                return el.name ?? el.id ?? JSON.stringify(el);
              return String(el);
            })
            .join("; ");
        }
        if (val && typeof val === "object") return JSON.stringify(val);
        return val ?? "";
      };

      /* ───────────── build CSV ───────────── */
      const headers = Object.keys(leases[0]);
      const csv = [
        headers.join(","),
        ...leases.map((lease) =>
          headers
            .map((h) => {
              const escaped = String(serialise(lease[h])).replace(/"/g, '""');
              return `"${escaped}"`;
            })
            .join(","),
        ),
      ].join("\n");

      /* ───────────── download ───────────── */
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leases_${companyName}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
      alert("Failed to export leases. Check console for details.");
    }
  };

  const handleEditLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));

    if (!Array.isArray(clone.wells)) clone.wells = [];
    if (!Array.isArray(clone.tanks)) clone.tanks = [];

    // Ensure newer fields exist for older records
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.County !== "string") clone.County = "";
    if (typeof clone.PurchaserLeaseNo !== "string") clone.PurchaserLeaseNo = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";
    if (clone.ExcludeFromPR !== "Y" && clone.ExcludeFromPR !== "N") {
      clone.ExcludeFromPR = "N";
    }

    // InjDispWellInfo (one-to-one extension; may be missing for non-INJ leases
    // or for INJ leases that haven't had H-10 setup yet).
    if (
      !clone.injDispWellInfo ||
      typeof clone.injDispWellInfo !== "object" ||
      Array.isArray(clone.injDispWellInfo)
    ) {
      clone.injDispWellInfo = null;
    }

    // Ensure newer well fields exist for older records
    clone.wells = clone.wells.map((well) => ({
      ...well,
      ElectricMeter:
        typeof well?.ElectricMeter === "string" ? well.ElectricMeter : "",
    }));

    // ints (default 0)
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(true);
    setActiveTab("basic");
    setShowLeaseModal(true);

    // reset sub-grid state
    setWellSearch("");
    setTankSearch("");
  };

  // Copy a lease minus wells/tanks arrays
  const handleCopyLease = (lease) => {
    const clone = JSON.parse(JSON.stringify(lease));
    delete clone.wells;
    delete clone.tanks;
    // H-10 setup is per-lease physical-well data; never copied
    clone.injDispWellInfo = null;

    clone.LeaseID = `${clone.LeaseID}-COPY`;

    // Ensure new fields exist
    if (typeof clone.UIC !== "string") clone.UIC = "";
    if (typeof clone.WaterTransport !== "string") clone.WaterTransport = "";
    if (
      clone.Target === null ||
      clone.Target === undefined ||
      clone.Target === ""
    )
      clone.Target = 0;
    if (
      clone.MaxInj === null ||
      clone.MaxInj === undefined ||
      clone.MaxInj === ""
    )
      clone.MaxInj = 0;
    if (
      clone.MaxPressure === null ||
      clone.MaxPressure === undefined ||
      clone.MaxPressure === ""
    )
      clone.MaxPressure = 0;

    setEditingLease(clone);
    setIsEditMode(false);
    setActiveTab("basic");
    setShowLeaseModal(true);

    setWellSearch("");
    setTankSearch("");
  };

  const handleCancelLease = () => {
    setEditingLease(null);
    setShowLeaseModal(false);
  };

  // -------------------------------
  // SAVE HELPERS
  // -------------------------------
  const normalizeLeaseForSave = (lease, allUsers) => {
    const usersArr = allUsers || [];
    const allUserIds = new Set(usersArr.map((u) => u.UserID));
    const validPumperIds = new Set(
      computeValidPumpers(usersArr).map((u) => u.UserID),
    );

    const out = { ...lease };

    // ----- FK fields -----
    if (!out.PumperID || !validPumperIds.has(out.PumperID)) {
      throw new Error("Invalid PumperID");
    }

    ["ReliefID", "Investor1", "Investor2"].forEach((k) => {
      if (out[k] === "") out[k] = null;
      else if (out[k] && !allUserIds.has(out[k])) out[k] = null;
    });

    // ----- Y/N flags -> 'Y'/'N'
    [
      "ShowOil",
      "ShowWater",
      "ShowGas",
      "ShowLACT",
      "ShowTbg",
      "ShowCsg",
      "ShowStatic",
      "ShowDiff",
      "ShowFlowRate",
      "ShowChoke",
      "ShowMcfAccum",
      "ShowHoursOn",
      "GasLift",
      "GasMeter",
      "WaterMeter",
      "Active",
      "ExcludeFromPR",
      "PumpOnMeter",
    ].forEach((k) => {
      if (k in out) {
        const v = out[k];
        out[k] = v === "Y" ? "Y" : v === "N" ? "N" : v ? "Y" : "N";
      }
    });

    // ExcludeFromPR only applies to OIL/GAS; drop it for INJ/WSW so we never
    // overwrite the DB value for well types where the flag isn't meaningful.
    {
      const wtUp = (out.WellType || "").toUpperCase();
      if (wtUp !== "OIL" && wtUp !== "GAS") {
        delete out.ExcludeFromPR;
      }
    }

    // ----- Trim to schema widths
    const trim = (s, n) => (typeof s === "string" ? s.slice(0, n) : s);
    out.LeaseName = trim(out.LeaseName, 35);
    out.GroupID = trim(out.GroupID, 20);
    out.Tag1 = out.Tag1 === "" ? null : trim(out.Tag1, 20);
    out.Tag2 = out.Tag2 === "" ? null : trim(out.Tag2, 20);
    out.Tag3 = out.Tag3 === "" ? null : trim(out.Tag3, 20);
    out.Tag4 = out.Tag4 === "" ? null : trim(out.Tag4, 20);
    out.RRC = trim(out.RRC, 6);
    out.API = trim(out.API, 30);
    out.FieldName = trim(out.FieldName, 100);
    out.Legal = trim(out.Legal, 255);
    out.PropertyNum = trim(out.PropertyNum, 10);
    out.CP = trim(out.CP, 10);
    out.UIC = trim(out.UIC, 9);
    out.County = trim(out.County, 50);
    out.PurchaserLeaseNo = trim(out.PurchaserLeaseNo, 50);
    out.WaterTransport = trim(out.WaterTransport, 50);

    // ----- Numerics
    ["PumperRoute", "ReportOrder"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    // ints (keep int)
    ["MaxInj", "MaxPressure", "Target"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
      else if (out[k] !== undefined) out[k] = parseInt(out[k], 10);
    });

    ["WIDecimal", "NetLeaseDecimal"].forEach((k) => {
      if (out[k] === "" || out[k] === null) delete out[k];
    });

    ["lat", "lon"].forEach((k) => {
      if (out[k] === "" || out[k] === null || isNaN(+out[k])) delete out[k];
      else out[k] = String(out[k]);
    });

    return out;
  };

  const handleSaveLease = async () => {
    if (!editingLease) return;

    if (!editingLease.LeaseID || !editingLease.LeaseID.trim()) {
      alert("A LeaseID is required.");
      return;
    }
    if (!editingLease.LeaseName || !editingLease.LeaseName.trim()) {
      alert("A LeaseName is required.");
      return;
    }

    const existsLocally = leases.some(
      (l) => l.LeaseID === editingLease.LeaseID,
    );
    if (!isEditMode && existsLocally) {
      alert(
        `LeaseID "${editingLease.LeaseID}" already exists.  Pick a unique ID.`,
      );
      return;
    }

    try {
      const payload = normalizeLeaseForSave(editingLease, users);
      const isInjection =
        (payload.WellType || editingLease.WellType || "").toUpperCase() ===
        "INJ";

      // Always include wells if present
      if (Array.isArray(editingLease.wells) && editingLease.wells.length > 0) {
        payload.wells = editingLease.wells.map((w) => ({
          ...w,
          LeaseID: editingLease.LeaseID,
          ElectricMeter:
            typeof w?.ElectricMeter === "string"
              ? w.ElectricMeter.slice(0, 50)
              : "",
        }));
      }

      // Only include tanks for NON-INJ
      if (
        !isInjection &&
        Array.isArray(editingLease.tanks) &&
        editingLease.tanks.length > 0
      ) {
        payload.tanks = editingLease.tanks.map((t) => ({
          ...t,
          LeaseID: editingLease.LeaseID,
        }));
      }

      // InjDispWellInfo (H-10 setup) — only relevant for INJ wells; only sent
      // when the user actually entered something OR a row already exists.
      if (isInjection) {
        const editing = editingLease.injDispWellInfo;
        const previousLease = leases.find(
          (l) => l.LeaseID === editingLease.LeaseID,
        );
        const hadExistingRow =
          !!previousLease &&
          previousLease.injDispWellInfo &&
          typeof previousLease.injDispWellInfo === "object";
        if (hadExistingRow || hasAnyInjDispWellInfo(editing)) {
          payload.injDispWellInfo =
            sanitizeInjDispWellInfoForSave(editing) || {};
        }
      }

      const isExisting = leases.some((l) => l.LeaseID === editingLease.LeaseID);
      let url, method;

      if (!isExisting) {
        method = "POST";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&createLease=1`;
      } else {
        method = "PATCH";
        url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}&lid=${encodeURIComponent(editingLease.LeaseID)}`;
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await response.text();
        throw new Error(
          `Lease save failed. Status: ${response.status}. ${msg}`,
        );
      }

      // The server returns the freshly-saved tanks for the touched lease
      // (incl. any rows it auto-added — e.g. WSW tank for WellType=WSW).
      // Use it to sync local state so the row shows up on reopen.
      const respJson = await response.json().catch(() => ({}));
      const serverTanks = Array.isArray(respJson?.tanks) ? respJson.tanks : null;

      // Update local state to mirror what was sent/saved.
      // Prefer the edited InjDispWellInfo object (full UI state) over the
      // sanitized payload version (which strips empty numerics for transport).
      const mergedInjDisp =
        editingLease.injDispWellInfo &&
        typeof editingLease.injDispWellInfo === "object"
          ? { ...editingLease.injDispWellInfo }
          : payload.injDispWellInfo || null;

      const finalLeaseID = String(editingLease.LeaseID || "");
      const tanksForFinalLease = serverTanks
        ? serverTanks.filter(
            (t) => String(t.LeaseID || "") === finalLeaseID,
          )
        : null;

      let updatedLeases;
      if (!isExisting) {
        updatedLeases = [
          ...leases,
          {
            ...editingLease,
            ...payload,
            injDispWellInfo: mergedInjDisp,
            ...(tanksForFinalLease ? { tanks: tanksForFinalLease } : {}),
          },
        ];
      } else {
        updatedLeases = leases.map((l) =>
          l.LeaseID === editingLease.LeaseID
            ? {
                ...l,
                ...payload,
                injDispWellInfo:
                  mergedInjDisp !== null
                    ? mergedInjDisp
                    : l.injDispWellInfo || null,
                ...(tanksForFinalLease ? { tanks: tanksForFinalLease } : {}),
              }
            : l,
        );
      }
      setLeases(updatedLeases);

      setShowLeaseModal(false);
    } catch (err) {
      console.error("Error saving lease:", err);
      alert(err.message || "Error saving lease. Check console for details.");
    }
  };

  // -------------------------------
  // WELLS / TANKS - ADD / REMOVE
  // -------------------------------
  const handleRemoveWell = async (well) => {
    if (!well.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w !== well),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove well "${well.WellID}" from server?`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&wid=${encodeURIComponent(well.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete well failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        wells: (prev.wells || []).filter((w) => w.UniqID !== well.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              wells: (lease.wells || []).filter(
                (w) => w.UniqID !== well.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing well:", err);
      setToast({
        visible: true,
        message:
          "Well has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleRemoveTank = async (tank) => {
    if (!tank.UniqID) {
      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t !== tank),
      }));
      return;
    }

    const confirmed = window.confirm(
      `Remove tank "${tank.TankID}" from server? This cannot be undone.`,
    );
    if (!confirmed) return;

    try {
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}&lid=${encodeURIComponent(
        editingLease.LeaseID,
      )}&tid=${encodeURIComponent(tank.UniqID)}`;

      const resp = await fetch(url, { method: "DELETE" });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(`Delete tank failed: ${resp.status} - ${msg}`);
      }

      setEditingLease((prev) => ({
        ...prev,
        tanks: (prev.tanks || []).filter((t) => t.UniqID !== tank.UniqID),
      }));

      setLeases((prevLeases) =>
        prevLeases.map((lease) => {
          if (lease.LeaseID === editingLease.LeaseID) {
            return {
              ...lease,
              tanks: (lease.tanks || []).filter(
                (t) => t.UniqID !== tank.UniqID,
              ),
            };
          }
          return lease;
        }),
      );
    } catch (err) {
      console.error("Error removing tank:", err);
      setToast({
        visible: true,
        message:
          "Tank has historical data and cannot be removed.  Please set Active to No to disable.",
      });
    }
  };

  const handleAddWell = () => {
    setEditingLease((prev) => {
      const newWell = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        WellID: "",
        Active: "Y",
        PropertyNum: "",
        AllocPct: "0.0000",
        RRCLeaseID: "",
        CP: "",
        ElectricMeter: "",
      };
      return { ...prev, wells: [newWell, ...(prev.wells || [])] };
    });
  };

  const handleAddTank = () => {
    setEditingLease((prev) => {
      const newTank = {
        UniqID: "",
        LeaseID: prev.LeaseID,
        TankID: "",
        Size: 0,
        BBLSperInch: "0.000",
        Active: "Y",
        TankType: "T",
        GasCoeff: "0.00",
        ExcludeDrawsFromProd: "N",
        WPTankNum: "",
      };
      return { ...prev, tanks: [newTank, ...(prev.tanks || [])] };
    });
  };

  // -------------------------------
  // FIELD HANDLERS
  // -------------------------------
  const handleLeaseFieldChange = (e) => {
    const { name, value } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeaseCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setEditingLease((prev) => ({ ...prev, [name]: checked ? "Y" : "N" }));
  };

  const handleInjDispWellInfoChange = (next) => {
    setEditingLease((prev) => ({ ...prev, injDispWellInfo: next || {} }));
  };

  // Normalize InjDispWellInfo for transport: strip empty strings on numeric
  // fields so they fall back to DB defaults rather than 0; keep keys whose
  // values are empty string only for free-text columns the user may have
  // intentionally cleared.
  const sanitizeInjDispWellInfoForSave = (info) => {
    if (!info || typeof info !== "object") return null;
    const numericKeys = new Set([
      "UICType",
      "InjIntervalDepthTop",
      "InjIntervalDepthBottom",
      "TbgPackerDepth",
      "InjThrough",
      "SWPct",
      "NORMPct",
      "FreshPct",
      "TotalCO2Pct",
      "AnthropogenicCO2Pct",
      "NGPct",
      "H2SPct",
      "PolymerPct",
      "SteamPct",
      "AirPct",
      "NitrogenPct",
      "FracWaterFBPct",
      "OtherPct",
      "OtherType1",
      "OtherType2",
      "OtherType3",
    ]);
    const out = {};
    Object.keys(info).forEach((k) => {
      if (k === "LeaseID") return; // server uses URL/lease key
      const v = info[k];
      if (v === undefined) return;
      if (numericKeys.has(k)) {
        if (v === "" || v === null) return; // omit -> preserve DB value/default
        const n = parseInt(v, 10);
        if (!isNaN(n)) out[k] = n;
        return;
      }
      out[k] = v;
    });
    return out;
  };

  // Heuristic: did the user actually edit anything in this section?
  // (used to skip sending it for never-touched non-INJ leases)
  const hasAnyInjDispWellInfo = (info) => {
    if (!info || typeof info !== "object") return false;
    return Object.keys(info).some((k) => {
      if (k === "LeaseID") return false;
      const v = info[k];
      return v !== "" && v !== null && v !== undefined;
    });
  };

  const handleWellChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newWells = [...(prev.wells || [])];
      newWells[index] = { ...newWells[index], [field]: value };
      return { ...prev, wells: newWells };
    });
  };

  const handleTankChange = (index, field, value) => {
    setEditingLease((prev) => {
      const newTanks = [...(prev.tanks || [])];
      newTanks[index] = { ...newTanks[index], [field]: value };
      return { ...prev, tanks: newTanks };
    });
  };

  // -------------------------------
  // SEARCH & SORT (leases)
  // -------------------------------
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey)
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredLeases = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = (leases || []).filter((lease) => {
      const activePass = showInactive || lease.Active === "Y";

      const matchesSearch =
        (lease.LeaseID || "").toLowerCase().includes(lowerSearch) ||
        (lease.LeaseName || "").toLowerCase().includes(lowerSearch) ||
        (lease.RRC || "").toLowerCase().includes(lowerSearch) ||
        (lease.WellType || "").toLowerCase().includes(lowerSearch) ||
        (lease.PumperID || "").toLowerCase().includes(lowerSearch) ||
        (lease.ReliefID || "").toLowerCase().includes(lowerSearch) ||
        (lease.CompanyName || "").toLowerCase().includes(lowerSearch) ||
        (lease.Subdomain || "").toLowerCase().includes(lowerSearch) ||
        String(lease.PumperRoute ?? "")
          .toLowerCase()
          .includes(lowerSearch);

      return activePass && matchesSearch;
    });

    return filtered.sort((a, b) => {
      const valA = a[sortBy] ?? "";
      const valB = b[sortBy] ?? "";

      if (typeof valA === "string" && typeof valB === "string") {
        const comparison = valA.localeCompare(valB);
        return sortDirection === "asc" ? comparison : -comparison;
      } else {
        if (valA < valB) return sortDirection === "asc" ? -1 : 1;
        if (valA > valB) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }
    });
  }, [leases, searchTerm, sortBy, sortDirection, showInactive]);

  // -------------------------------
  // SEARCH & SORT (wells)
  // -------------------------------
  const handleWellSort = (columnKey) => {
    if (wellSortBy === columnKey)
      setWellSortDirection(wellSortDirection === "asc" ? "desc" : "asc");
    else {
      setWellSortBy(columnKey);
      setWellSortDirection("asc");
    }
  };

  const displayedWells = useMemo(() => {
    const arr = Array.isArray(editingLease?.wells) ? editingLease.wells : [];
    const lower = (wellSearch || "").toLowerCase();

    let rows = arr.map((well, idx) => ({ well, idx }));

    rows = rows.filter(({ well }) => {
      const activePass = showInactiveWells || (well.Active || "Y") === "Y";
      const matches =
        String(well.WellID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.PropertyNum || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.RRCLeaseID || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.CP || "")
          .toLowerCase()
          .includes(lower) ||
        String(well.ElectricMeter || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.well?.[wellSortBy] ?? "";
      const vb = b.well?.[wellSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return wellSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.wells,
    wellSearch,
    wellSortBy,
    wellSortDirection,
    showInactiveWells,
  ]);

  // -------------------------------
  // SEARCH & SORT (tanks)
  // -------------------------------
  const handleTankSort = (columnKey) => {
    if (tankSortBy === columnKey)
      setTankSortDirection(tankSortDirection === "asc" ? "desc" : "asc");
    else {
      setTankSortBy(columnKey);
      setTankSortDirection("asc");
    }
  };

  const displayedTanks = useMemo(() => {
    const arr = Array.isArray(editingLease?.tanks) ? editingLease.tanks : [];
    const lower = (tankSearch || "").toLowerCase();

    let rows = arr.map((tank, idx) => ({ tank, idx }));

    rows = rows.filter(({ tank }) => {
      const activePass = showInactiveTanks || (tank.Active || "Y") === "Y";
      const matches =
        String(tank.TankID || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.TankType || "")
          .toLowerCase()
          .includes(lower) ||
        String(tank.WPTankNum || "")
          .toLowerCase()
          .includes(lower);
      return activePass && matches;
    });

    rows.sort((a, b) => {
      const va = a.tank?.[tankSortBy] ?? "";
      const vb = b.tank?.[tankSortBy] ?? "";
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb);
      return tankSortDirection === "asc" ? cmp : -cmp;
    });

    return rows;
  }, [
    editingLease?.tanks,
    tankSearch,
    tankSortBy,
    tankSortDirection,
    showInactiveTanks,
  ]);

  // -------------------------------
  // OPTIONS
  // -------------------------------
  const pumperOptions = validPumpers;
  const reliefOptions = validPumpers;
  const investorOptions = (users || []).filter((user) => user.Role === "I");

  // -------------------------------
  // Leases Mass Change actions (Freshdesk #1244)
  // -------------------------------
  const mcFieldDef =
    MASS_CHANGE_FIELDS.find((f) => f.key === mcField) || null;

  const openMassChange = () => {
    setMcField("");
    setMcBeforeSel(MC_BEFORE_ANY);
    setMcBeforeOptions(null);
    setMcAfterIsNull(false);
    setMcAfterValue("");
    setMcFilterStatus("");
    setMcFilterPumper("");
    setMcFilterRelief("");
    setMcFilterGroup("");
    setMcFilterDistrict("");
    setMcFilterRoute("");
    setMcFilterNameSearch("");
    setMcPreview(null);
    setMcResult(null);
    setShowMassChangeModal(true);
  };

  const closeMassChange = () => {
    if (!mcBusy) setShowMassChangeModal(false);
  };

  // Load the unique existing values for the selected field (powers the
  // data-driven "before value" dropdown). Re-runs whenever the field changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showMassChangeModal || !mcField) {
      setMcBeforeOptions(null);
      return;
    }
    let cancelled = false;
    setMcBeforeLoading(true);
    setMcBeforeOptions(null);
    setMcBeforeSel(MC_BEFORE_ANY);
    (async () => {
      try {
        const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          companyName,
        )}`;
        const resp = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "leaseFieldValues", field: mcField }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok) throw new Error(data?.error || `Failed: ${resp.status}`);
        setMcBeforeOptions({
          values: Array.isArray(data.values) ? data.values : [],
          hasNull: !!data.hasNull,
          hasBlank: !!data.hasBlank,
        });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setMcBeforeOptions({
          values: [],
          hasNull: false,
          hasBlank: false,
          error: true,
        });
      } finally {
        if (!cancelled) setMcBeforeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mcField, showMassChangeModal, companyName]);

  // Any input change invalidates a stale preview so it can't be applied.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setMcPreview(null);
  }, [
    mcField,
    mcBeforeSel,
    mcAfterIsNull,
    mcAfterValue,
    mcFilterStatus,
    mcFilterPumper,
    mcFilterRelief,
    mcFilterGroup,
    mcFilterDistrict,
    mcFilterRoute,
    mcFilterNameSearch,
  ]);

  // Decodes the encoded "before value" dropdown selection into API fields.
  const mcDecodeBefore = () => {
    if (mcBeforeSel === MC_BEFORE_NULL) {
      return { beforeIsNull: true, beforeIsBlank: false, beforeValue: "" };
    }
    if (mcBeforeSel === MC_BEFORE_BLANK) {
      return { beforeIsNull: false, beforeIsBlank: true, beforeValue: "" };
    }
    if (mcBeforeSel.startsWith(MC_BEFORE_VALUE_PREFIX)) {
      return {
        beforeIsNull: false,
        beforeIsBlank: false,
        beforeValue: mcBeforeSel.slice(MC_BEFORE_VALUE_PREFIX.length),
      };
    }
    return { beforeIsNull: false, beforeIsBlank: false, beforeValue: "" };
  };

  const mcDescribeBefore = () => {
    if (mcBeforeSel === MC_BEFORE_NULL) return "NULL";
    if (mcBeforeSel === MC_BEFORE_BLANK) return "(blank / empty string)";
    if (mcBeforeSel.startsWith(MC_BEFORE_VALUE_PREFIX)) {
      return mcBeforeSel.slice(MC_BEFORE_VALUE_PREFIX.length);
    }
    return "(any — scoped by filters only)";
  };

  const mcBuildBody = (extra) => {
    const b = mcDecodeBefore();
    return {
      action: "leasesMassChange",
      field: mcField,
      beforeIsNull: b.beforeIsNull,
      beforeIsBlank: b.beforeIsBlank,
      beforeValue: b.beforeValue,
      afterIsNull: mcAfterIsNull,
      afterValue: mcAfterIsNull ? "" : mcAfterValue,
      filters: {
        status: mcFilterStatus,
        pumperID: mcFilterPumper,
        reliefID: mcFilterRelief,
        groupID: mcFilterGroup,
        district: mcFilterDistrict,
        route: mcFilterRoute,
        leaseNameSearch: mcFilterNameSearch,
      },
      ...extra,
    };
  };

  const mcValidate = () => {
    if (!mcField) return "Please choose a field to change.";
    const hasFilter =
      !!mcFilterStatus ||
      !!mcFilterPumper ||
      !!mcFilterRelief ||
      !!mcFilterGroup ||
      !!mcFilterDistrict ||
      !!mcFilterRoute ||
      !!mcFilterNameSearch;
    const hasBeforeCond = mcBeforeSel !== MC_BEFORE_ANY;
    if (!hasBeforeCond && !hasFilter) {
      return "Pick a 'before value' (or NULL / blank) or at least one filter so the change is scoped.";
    }
    if (!mcAfterIsNull && mcAfterValue === "") {
      return "Set an 'after value' (or check NULL).";
    }
    return null;
  };

  const mcRunPreview = async () => {
    const err = mcValidate();
    if (err) {
      alert(err);
      return;
    }
    try {
      setMcBusy(true);
      setMcResult(null);
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}`;
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mcBuildBody({ preview: true })),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || `Preview failed: ${resp.status}`);
      }
      setMcPreview(data);
    } catch (e) {
      console.error(e);
      alert(`Preview failed. ${e.message || "See console for details."}`);
    } finally {
      setMcBusy(false);
    }
  };

  const mcApply = async () => {
    if (!mcPreview) {
      alert("Run Preview first.");
      return;
    }
    if (!mcPreview.matchCount) {
      alert("No leases match the current selection — nothing to change.");
      return;
    }
    const fieldLabel = mcFieldDef?.label || mcField;
    const newValDisp = mcAfterIsNull ? "NULL (empty)" : mcAfterValue;
    const sure = window.confirm(
      `WARNING — this will permanently update ${mcPreview.matchCount} lease(s).\n\n` +
        `Field: ${fieldLabel}\n` +
        `Before value: ${mcDescribeBefore()}\n` +
        `New value: ${newValDisp}\n\n` +
        `This cannot be undone. Continue?`,
    );
    if (!sure) return;

    try {
      setMcBusy(true);
      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        companyName,
      )}`;
      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(mcBuildBody({ confirm: true })),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || `Update failed: ${resp.status}`);
      }

      const affected = new Set(data.affectedLeaseIds || []);
      const newVal = mcAfterIsNull ? null : mcAfterValue;
      setLeases((prev) =>
        prev.map((l) =>
          affected.has(l.LeaseID) ? { ...l, [mcField]: newVal } : l,
        ),
      );

      setMcResult(data);
      setMcPreview(null);
    } catch (e) {
      console.error(e);
      alert(`Update failed. ${e.message || "See console for details."}`);
    } finally {
      setMcBusy(false);
    }
  };

  // Renders the "after value" input based on the selected field's type.
  const renderMcAfterInput = () => {
    const inputType = mcFieldDef?.input || "text";

    if (mcAfterIsNull) {
      return (
        <input
          className={getInputClasses(isDark)}
          value="(NULL / empty)"
          disabled
        />
      );
    }
    if (inputType === "pumper") {
      return (
        <select
          value={mcAfterValue}
          onChange={(e) => setMcAfterValue(e.target.value)}
          className={getInputClasses(isDark)}
          disabled={mcBusy || !mcField}
        >
          <option value="">-- Select --</option>
          {validPumpers?.map((u) => (
            <option key={u.UserID} value={u.UserID}>
              {u.UserID} - {u.FullName}
            </option>
          ))}
        </select>
      );
    }
    if (inputType === "active") {
      return (
        <select
          value={mcAfterValue}
          onChange={(e) => setMcAfterValue(e.target.value)}
          className={getInputClasses(isDark)}
          disabled={mcBusy || !mcField}
        >
          <option value="">-- Select --</option>
          <option value="Y">Y (Active)</option>
          <option value="N">N (Inactive)</option>
        </select>
      );
    }
    // Free-text field: allow typing a new value OR picking an existing one
    // (the datalist is populated from the same unique-values lookup).
    const listId = `mc-after-list-${mcField}`;
    const existing = mcBeforeOptions?.values || [];
    return (
      <>
        <input
          type="text"
          list={existing.length ? listId : undefined}
          value={mcAfterValue}
          onChange={(e) => setMcAfterValue(e.target.value)}
          className={getInputClasses(isDark)}
          disabled={mcBusy || !mcField}
          placeholder="Pick an existing value or type a new one"
        />
        {existing.length > 0 && (
          <datalist id={listId}>
            {existing.map((v) => (
              <option key={`af:${v}`} value={String(v)} />
            ))}
          </datalist>
        )}
      </>
    );
  };

  // Builds the option list for the data-driven "before value" dropdown.
  const renderMcBeforeOptions = () => {
    const opts = [
      <option key="__any__" value={MC_BEFORE_ANY}>
        -- Any (scope by filters only) --
      </option>,
    ];
    if (mcBeforeOptions?.hasNull) {
      opts.push(
        <option key="__null__" value={MC_BEFORE_NULL}>
          (NULL)
        </option>,
      );
    }
    if (mcBeforeOptions?.hasBlank) {
      opts.push(
        <option key="__blank__" value={MC_BEFORE_BLANK}>
          (blank / empty string)
        </option>,
      );
    }
    (mcBeforeOptions?.values || []).forEach((v) => {
      opts.push(
        <option key={`v:${v}`} value={`${MC_BEFORE_VALUE_PREFIX}${v}`}>
          {String(v)}
        </option>,
      );
    });
    return opts;
  };

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <div className="p-2">
      {/* Top bar: search & actions */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3">
        <div className="flex-1 flex items-center gap-3">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-2.5 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            />
            <input
              type="text"
              placeholder="Search leases..."
              value={searchTerm}
              onChange={handleSearchChange}
              className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? "bg-gray-700 text-gray-100 border border-gray-600"
                  : "bg-white text-gray-700 border border-gray-300"
              }`}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={openMassChange}
            className="inline-flex items-center px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white"
            title="Bulk-update a whitelisted lease field across filtered leases (preview before apply)"
          >
            Leases Mass Change
          </button>

          <button
            onClick={handleCreateLease}
            className="inline-flex items-center px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            New
          </button>

          <button
            onClick={handleExportLeases}
            className="inline-flex items-center px-3 py-1 rounded bg-teal-500 hover:bg-teal-600 text-white"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-1" />
            Export
          </button>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none ml-2">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className={isDark ? "text-gray-200" : "text-gray-700"}>
              Show Inactive
            </span>
          </label>
        </div>
      </div>

      {/* Leases Table */}
      {sortedAndFilteredLeases && sortedAndFilteredLeases.length > 0 ? (
        <div className="overflow-x-auto rounded-md shadow-sm">
          <table className="min-w-full text-sm">
            <thead className={getHeaderClasses()}>
              <tr>
                <SortableHeader
                  label="LeaseID"
                  columnKey="LeaseID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="LeaseName"
                  columnKey="LeaseName"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="RRC #"
                  columnKey="RRC"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="WellType"
                  columnKey="WellType"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Pumper"
                  columnKey="PumperID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Route"
                  columnKey="PumperRoute"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Relief"
                  columnKey="ReliefID"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <SortableHeader
                  label="Active"
                  columnKey="Active"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  theme={theme}
                />
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredLeases.map((lease, idx) => (
                <tr key={idx} className={getRowClasses()}>
                  <td className="py-2 px-4">{lease.LeaseID}</td>
                  <td className="py-2 px-4">{lease.LeaseName}</td>
                  <td className="py-2 px-4">{lease.RRC}</td>
                  <td className="py-2 px-4">{lease.WellType}</td>
                  <td className="py-2 px-4">{lease.PumperID || ""}</td>
                  <td className="py-2 px-4">{lease.PumperRoute ?? ""}</td>
                  <td className="py-2 px-4">{lease.ReliefID || ""}</td>
                  <td className="py-2 px-4 text-right">
                    {lease.Active === "Y" ? <span title="Active">✓</span> : ""}
                  </td>
                  <td className="py-2 px-4">
                    <button
                      onClick={() => handleEditLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-green-500 hover:bg-green-600 text-white"
                    >
                      <FontAwesomeIcon icon={faEdit} className="mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleCopyLease(lease)}
                      className="inline-flex items-center px-2 py-1 rounded bg-purple-500 hover:bg-purple-600 text-white ml-2"
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-1" />
                      Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={`${isDark ? "text-gray-300" : "text-gray-500"} mt-4`}>
          No leases found.
        </p>
      )}

      {/* Lease Modal */}
      {showLeaseModal && editingLease && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-40 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-50"
          }`}
        >
          <div
            className={`w-full max-w-5xl p-4 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            {/* Sticky header */}
            <div
              className={`sticky top-0 z-10 px-4 py-3 border-b flex justify-between items-center ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <h4 className="font-semibold text-sm">
                {editingLease.LeaseID || "(New)"} –{" "}
                {editingLease.LeaseName || "Unnamed Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full hover:bg-gray-500/20"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Modal Title */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">
                {editingLease.LeaseID &&
                leases.some((l) => l.LeaseID === editingLease.LeaseID)
                  ? "Edit Lease"
                  : "Create Lease"}
              </h4>
              <button
                onClick={handleCancelLease}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-300"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            {/* Tab Buttons */}
            {(() => {
              const isInjection =
                (editingLease.WellType || "").toUpperCase() === "INJ";
              return (
                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveTab("basic")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "basic"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Basic Info
                  </button>

                  <button
                    onClick={() => setActiveTab("additional")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "additional"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Additional Info
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("purchasers")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "purchasers"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Purchasers
                    </button>
                  )}

                  {isInjection && (
                    <button
                      onClick={() => setActiveTab("injection")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "injection"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Injection
                    </button>
                  )}

                  <button
                    onClick={() => setActiveTab("wells")}
                    className={`px-3 py-1 rounded ${
                      activeTab === "wells"
                        ? "bg-blue-500 text-white"
                        : isDark
                          ? "bg-gray-700 text-gray-100"
                          : "bg-gray-300 text-gray-800"
                    }`}
                  >
                    Wells
                  </button>

                  {!isInjection && (
                    <button
                      onClick={() => setActiveTab("tanks")}
                      className={`px-3 py-1 rounded ${
                        activeTab === "tanks"
                          ? "bg-blue-500 text-white"
                          : isDark
                            ? "bg-gray-700 text-gray-100"
                            : "bg-gray-300 text-gray-800"
                      }`}
                    >
                      Tanks
                    </button>
                  )}
                </div>
              );
            })()}
            {/* BASIC */}
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Lease ID</label>
                  <input
                    name="LeaseID"
                    value={editingLease.LeaseID}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    readOnly={isEditMode}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Lease Name</label>
                  <input
                    name="LeaseName"
                    value={editingLease.LeaseName}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Active?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="leaseActiveToggle"
                      type="checkbox"
                      name="Active"
                      checked={editingLease.Active === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="leaseActiveToggle" className="font-medium">
                      Enabled
                    </label>
                  </div>
                </div>

                {/* Pumper, Route */}
                <div>
                  <label className="block mb-1 font-semibold">Pumper</label>
                  <select
                    name="PumperID"
                    value={editingLease.PumperID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {pumperOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Pumper Route
                  </label>
                  <input
                    type="number"
                    name="PumperRoute"
                    min="0"
                    step="1"
                    value={editingLease.PumperRoute ?? ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g. 1, 2, 3…"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>

                {/* Relief */}
                <div>
                  <label className="block mb-1 font-semibold">Relief</label>
                  <select
                    name="ReliefID"
                    value={editingLease.ReliefID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {reliefOptions
                      ?.filter((u) => u.UserID !== "admin")
                      .map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Group, WellType */}
                <div>
                  <label className="block mb-1 font-semibold">Group</label>
                  <input
                    type="text"
                    name="GroupID"
                    value={editingLease.GroupID || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">WellType</label>
                  <select
                    name="WellType"
                    value={editingLease.WellType || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="OIL">OIL</option>
                    <option value="GAS">GAS</option>
                    <option value="INJ">INJ</option>
                    <option value="WSW">WSW</option>
                  </select>
                </div>

                {/* Show flags */}
                <div>
                  <label className="block mb-1 font-semibold">Show Oil?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showOilToggle"
                      type="checkbox"
                      name="ShowOil"
                      checked={editingLease.ShowOil === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showOilToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Show Gas?</label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showGasToggle"
                      type="checkbox"
                      name="ShowGas"
                      checked={editingLease.ShowGas === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showGasToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    Show Water?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="showWaterToggle"
                      type="checkbox"
                      name="ShowWater"
                      checked={editingLease.ShowWater === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="showWaterToggle" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                {/* Water Meter, District */}
                <div>
                  <label className="block mb-1 font-semibold">
                    Use Water Meter?
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="useWaterMeter"
                      type="checkbox"
                      name="WaterMeter"
                      checked={editingLease.WaterMeter === "Y"}
                      onChange={handleLeaseCheckboxChange}
                      className="accent-blue-600 dark:accent-blue-400"
                    />
                    <label htmlFor="useWaterMeter" className="font-medium">
                      Enable
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">District</label>
                  <input
                    name="District"
                    value={editingLease.District || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                {/* RRC, CP, County, FieldName */}
                <div>
                  <label className="block mb-1 font-semibold">RRC</label>
                  <input
                    name="RRC"
                    value={editingLease.RRC || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">CP</label>
                  <input
                    name="CP"
                    value={editingLease.CP || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">County</label>
                  <input
                    name="County"
                    value={editingLease.County || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Howard"
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Field Name</label>
                  <input
                    name="FieldName"
                    value={editingLease.FieldName || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                    placeholder="e.g., Spraberry"
                  />
                </div>

                {/* Exclude from PR — only OIL/GAS; hidden for INJ/WSW */}
                {["OIL", "GAS"].includes(
                  (editingLease.WellType || "").toUpperCase(),
                ) && (
                  <div
                    className="flex items-end justify-end"
                    title="Exclude this lease from PR reporting calculations"
                  >
                    <div className="flex items-center gap-2 pb-1">
                      <input
                        id="excludeFromPRToggle"
                        type="checkbox"
                        name="ExcludeFromPR"
                        checked={editingLease.ExcludeFromPR === "Y"}
                        onChange={handleLeaseCheckboxChange}
                        className="accent-blue-600 dark:accent-blue-400"
                      />
                      <label
                        htmlFor="excludeFromPRToggle"
                        className="font-semibold"
                      >
                        Exclude from PR
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* ADDITIONAL (Tags/Investors/External Property #) */}
            {activeTab === "additional" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                <div>
                  <label className="block mb-1 font-semibold">Tag 1</label>
                  <select
                    name="Tag1"
                    value={editingLease.Tag1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 2</label>
                  <select
                    name="Tag2"
                    value={editingLease.Tag2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 3</label>
                  <select
                    name="Tag3"
                    value={editingLease.Tag3 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Tag 4</label>
                  <select
                    name="Tag4"
                    value={editingLease.Tag4 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- None --</option>
                    {tags?.map((t) => (
                      <option key={t.TagID} value={t.TagID}>
                        {t.TagDesc}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 1</label>
                  <select
                    name="Investor1"
                    value={editingLease.Investor1 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Investor 2</label>
                  <select
                    name="Investor2"
                    value={editingLease.Investor2 || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  >
                    <option value="">-- Select --</option>
                    {investorOptions?.map((u) => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.UserID} - {u.FullName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-semibold">
                    External Property #
                  </label>
                  <input
                    name="PropertyNum"
                    value={editingLease.PropertyNum || ""}
                    onChange={handleLeaseFieldChange}
                    className={getInputClasses(isDark)}
                  />
                </div>
              </div>
            )}
            {/* PURCHASERS (hidden for INJ) */}
            {activeTab === "purchasers" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4 text-sm">
                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser
                    </label>
                    <select
                      name="Purchaser"
                      value={editingLease.Purchaser || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    >
                      <option value="">-- Select --</option>
                      {purchasers?.map((p) => (
                        <option key={p.PurchaserID} value={p.PurchaserID}>
                          {p.PurchaserID} - {p.PurchaserName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Purchaser Lease No
                    </label>
                    <input
                      name="PurchaserLeaseNo"
                      value={editingLease.PurchaserLeaseNo || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                    />
                  </div>

                  <div>
                    <label className="block mb-1 font-semibold">
                      Water Transport
                    </label>
                    <input
                      name="WaterTransport"
                      value={editingLease.WaterTransport || ""}
                      onChange={handleLeaseFieldChange}
                      className={getInputClasses(isDark)}
                      placeholder="e.g., ABC Water Hauling"
                    />
                  </div>
                </div>
              )}
            {/* INJECTION (only when WellType == INJ) */}
            {activeTab === "injection" &&
              (editingLease.WellType || "").toUpperCase() === "INJ" && (
                <div className="mb-4 text-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="block mb-1 font-semibold">UIC</label>
                      <input
                        id="uicInput"
                        type="text"
                        name="UIC"
                        value={editingLease.UIC || ""}
                        onChange={handleLeaseFieldChange}
                        maxLength={9}
                        className={getInputClasses(isDark)}
                        placeholder="e.g., 123456789"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 font-semibold">
                        Daily Target
                      </label>
                      <input
                        type="number"
                        name="Target"
                        min="0"
                        step="1"
                        value={editingLease.Target ?? 0}
                        onChange={handleLeaseFieldChange}
                        className={getInputClasses(isDark)}
                        placeholder="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        title="Integer daily target (e.g., 0, 25, 300)"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 font-semibold">
                        Max Inj
                      </label>
                      <input
                        type="number"
                        name="MaxInj"
                        min="0"
                        step="1"
                        value={editingLease.MaxInj ?? 0}
                        onChange={handleLeaseFieldChange}
                        className={getInputClasses(isDark)}
                        placeholder="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>

                    <div>
                      <label className="block mb-1 font-semibold">
                        Max Pressure
                      </label>
                      <input
                        type="number"
                        name="MaxPressure"
                        min="0"
                        step="1"
                        value={editingLease.MaxPressure ?? 0}
                        onChange={handleLeaseFieldChange}
                        className={getInputClasses(isDark)}
                        placeholder="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                      />
                    </div>
                  </div>

                  <InjDispWellInfoSection
                    value={editingLease.injDispWellInfo || {}}
                    onChange={handleInjDispWellInfoChange}
                    theme={theme}
                  />
                </div>
              )}
            {/* WELLS (grid-style like leases) */}
            {activeTab === "wells" && (
              <div className="mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 mb-2">
                  <h5 className="font-semibold">Wells</h5>

                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <div className="relative">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className={`absolute left-3 top-2.5 ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      />
                      <input
                        type="text"
                        placeholder="Search wells..."
                        value={wellSearch}
                        onChange={(e) => setWellSearch(e.target.value)}
                        className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark
                            ? "bg-gray-700 text-gray-100 border border-gray-600"
                            : "bg-white text-gray-700 border border-gray-300"
                        }`}
                      />
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={showInactiveWells === true}
                        onChange={(e) => setShowInactiveWells(e.target.checked)}
                      />
                      <span
                        className={isDark ? "text-gray-200" : "text-gray-700"}
                      >
                        Show Inactive
                      </span>
                    </label>

                    <button
                      onClick={handleAddWell}
                      className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 whitespace-nowrap"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1" />
                      Add Well
                    </button>
                  </div>
                </div>

                {displayedWells.length > 0 ? (
                  <div className="w-full rounded-md shadow-sm overflow-hidden">
                    <table className="w-full table-auto text-sm">
                      <thead className={getHeaderClasses()}>
                        <tr>
                          <SortableHeader
                            label="WellID"
                            columnKey="WellID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Active"
                            columnKey="Active"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="External Well #"
                            columnKey="PropertyNum"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Fixed Alloc Pct"
                            columnKey="AllocPct"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="API #"
                            columnKey="API"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="RRC"
                            columnKey="RRCLeaseID"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="Electric Meter #"
                            columnKey="ElectricMeter"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <SortableHeader
                            label="CP #"
                            columnKey="CP"
                            sortBy={wellSortBy}
                            sortDirection={wellSortDirection}
                            onSort={handleWellSort}
                            theme={theme}
                          />
                          <th className="py-2 px-2 text-left whitespace-nowrap">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayedWells.map(({ well, idx }) => (
                          <tr
                            key={well.UniqID || `well-${idx}`}
                            className={getRowClasses()}
                          >
                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.WellID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "WellID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <select
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.Active || "Y"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "Active",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="Y">Yes</option>
                                <option value="N">No</option>
                              </select>
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.PropertyNum || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "PropertyNum",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.AllocPct || "0.0000"}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "AllocPct",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(
                                  isDark,
                                )} w-[11ch] max-w-full min-w-0 whitespace-nowrap`}
                                value={well.API || ""}
                                maxLength={11}
                                inputMode="numeric"
                                pattern="\d*"
                                onChange={(e) =>
                                  handleWellChange(idx, "API", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.RRCLeaseID || ""}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "RRCLeaseID",
                                    e.target.value,
                                  )
                                }
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.ElectricMeter || ""}
                                maxLength={50}
                                onChange={(e) =>
                                  handleWellChange(
                                    idx,
                                    "ElectricMeter",
                                    e.target.value,
                                  )
                                }
                                placeholder="Meter #"
                              />
                            </td>

                            <td className="py-2 px-2">
                              <input
                                className={`${getInputClasses(isDark)} w-full min-w-0`}
                                value={well.CP || ""}
                                onChange={(e) =>
                                  handleWellChange(idx, "CP", e.target.value)
                                }
                              />
                            </td>

                            <td className="py-2 px-2 whitespace-nowrap">
                              <button
                                onClick={() => handleRemoveWell(well)}
                                className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs whitespace-nowrap"
                              >
                                <FontAwesomeIcon
                                  icon={faTrash}
                                  className="mr-1"
                                />
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {toast.visible && (
                      <div className="fixed inset-0 flex items-center justify-center z-50">
                        <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                          <div className="flex items-center justify-between">
                            <span>{toast.message}</span>
                            <button
                              onClick={() =>
                                setToast({ ...toast, visible: false })
                              }
                              className="ml-4"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic">No wells found.</p>
                )}
              </div>
            )}
            {/* TANKS (grid-style like leases) — hidden for INJ */}
            {activeTab === "tanks" &&
              (editingLease.WellType || "").toUpperCase() !== "INJ" && (
                <div className="mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                    <h5 className="font-semibold">Tanks</h5>

                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faSearch}
                          className={`absolute left-3 top-2.5 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        />
                        <input
                          type="text"
                          placeholder="Search tanks..."
                          value={tankSearch}
                          onChange={(e) => setTankSearch(e.target.value)}
                          className={`pl-9 pr-4 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDark
                              ? "bg-gray-700 text-gray-100 border border-gray-600"
                              : "bg-white text-gray-700 border border-gray-300"
                          }`}
                        />
                      </div>

                      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={showInactiveTanks === true}
                          onChange={(e) =>
                            setShowInactiveTanks(e.target.checked)
                          }
                        />
                        <span
                          className={isDark ? "text-gray-200" : "text-gray-700"}
                        >
                          Show Inactive
                        </span>
                      </label>

                      <button
                        onClick={handleAddTank}
                        className="inline-flex items-center px-2 py-1 rounded bg-gray-300 hover:bg-gray-400 text-gray-800"
                      >
                        <FontAwesomeIcon icon={faPlus} className="mr-1" />
                        Add Tank
                      </button>
                    </div>
                  </div>

                  {displayedTanks.length > 0 ? (
                    <div className="overflow-x-auto rounded-md shadow-sm">
                      <table className="min-w-full text-sm">
                        <thead className={getHeaderClasses()}>
                          <tr>
                            <SortableHeader
                              label="TankID"
                              columnKey="TankID"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Active"
                              columnKey="Active"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Size"
                              columnKey="Size"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="BBLS/Inch"
                              columnKey="BBLSperInch"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Type"
                              columnKey="TankType"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="GasCoeff"
                              columnKey="GasCoeff"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="Exclude Draws"
                              columnKey="ExcludeDrawsFromProd"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <SortableHeader
                              label="External Tank #"
                              columnKey="WPTankNum"
                              sortBy={tankSortBy}
                              sortDirection={tankSortDirection}
                              onSort={handleTankSort}
                              theme={theme}
                            />
                            <th className="py-2 px-4 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedTanks.map(({ tank, idx }) => (
                            <tr
                              key={tank.UniqID || `tank-${idx}`}
                              className={getRowClasses()}
                            >
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.TankID || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankID",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.25rem]`}
                                  value={tank.Active || "Y"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Active",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="Y">Yes</option>
                                  <option value="N">No</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.Size ?? 0}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "Size",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  step="0.001"
                                  className={getInputClasses(isDark)}
                                  value={tank.BBLSperInch || "0.000"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "BBLSperInch",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={`${getInputClasses(
                                    isDark,
                                  )} min-w-[5.75rem]`}
                                  value={tank.TankType || "T"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "TankType",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="T">Tank</option>
                                  <option value="W">Water</option>
                                  <option value="F">Frac</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  type="number"
                                  className={getInputClasses(isDark)}
                                  value={tank.GasCoeff || "0.00"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "GasCoeff",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <select
                                  className={getInputClasses(isDark)}
                                  value={tank.ExcludeDrawsFromProd || "N"}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "ExcludeDrawsFromProd",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="N">No</option>
                                  <option value="Y">Yes</option>
                                </select>
                              </td>
                              <td className="py-2 px-4">
                                <input
                                  className={getInputClasses(isDark)}
                                  value={tank.WPTankNum || ""}
                                  onChange={(e) =>
                                    handleTankChange(
                                      idx,
                                      "WPTankNum",
                                      e.target.value,
                                    )
                                  }
                                />
                              </td>
                              <td className="py-2 px-4">
                                <button
                                  onClick={() => {
                                    const hasHistory =
                                      tank?.HasHistory === true ||
                                      (typeof tank?.HasHistory === "string" &&
                                        tank.HasHistory.toUpperCase() ===
                                          "Y") ||
                                      Number(
                                        tank?.HistoryCount ??
                                          tank?.HistoricalCount ??
                                          tank?.ProdCount ??
                                          tank?.DrawCount ??
                                          0,
                                      ) > 0;

                                    if (hasHistory) {
                                      setToast({
                                        visible: true,
                                        message:
                                          "Cannot remove this tank because it has historical data. Please set Active to 'No' instead.",
                                      });
                                      return;
                                    }

                                    handleRemoveTank(tank);
                                  }}
                                  className="inline-flex items-center px-2 py-1 rounded bg-red-500 hover:bg-red-600 text-white text-xs"
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="mr-1"
                                  />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {toast.visible && (
                        <div className="fixed inset-0 flex items-center justify-center z-50">
                          <div className="bg-red-500 text-white p-4 rounded shadow-lg">
                            <div className="flex items-center justify-between">
                              <span>{toast.message}</span>
                              <button
                                onClick={() =>
                                  setToast({ ...toast, visible: false })
                                }
                                className="ml-4"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm italic">No tanks found.</p>
                  )}
                </div>
              )}
            {/* Footer */}
            <div className="mt-3 flex justify-end space-x-2">
              <button
                onClick={handleCancelLease}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLease}
                className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                Save Lease
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leases Mass Change Modal (Freshdesk #1244 — supersedes Replace Pumper) */}
      {showMassChangeModal && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-50 ${
            isDark ? "bg-black bg-opacity-80" : "bg-black bg-opacity-40"
          }`}
        >
          <div
            className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 rounded-lg shadow-2xl relative ${
              isDark ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
            }`}
          >
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-bold">Leases Mass Change</h4>
              <button
                onClick={closeMassChange}
                className="p-2 rounded-full hover:bg-gray-500/20"
                disabled={mcBusy}
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <p className="text-sm mb-3">
              Bulk-update a single approved lease field. Choose the field, set
              the <strong>before</strong> and <strong>after</strong> values,
              apply filters to scope which leases are affected, then{" "}
              <strong>Preview</strong> before applying.
            </p>

            <div className="text-sm space-y-4">
              {/* Step 1 — Choose field */}
              <div>
                <label className="block mb-1 font-semibold">
                  1. Field to change
                </label>
                <select
                  value={mcField}
                  onChange={(e) => {
                    setMcField(e.target.value);
                    setMcBeforeSel(MC_BEFORE_ANY);
                    setMcAfterValue("");
                    setMcAfterIsNull(false);
                  }}
                  className={getInputClasses(isDark)}
                  disabled={mcBusy}
                >
                  <option value="">-- Select a field --</option>
                  {MASS_CHANGE_FIELDS.map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Step 2 / 3 — Before & After values */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1 font-semibold">
                    2. Before value (match)
                  </label>
                  <select
                    value={mcBeforeSel}
                    onChange={(e) => setMcBeforeSel(e.target.value)}
                    className={getInputClasses(isDark)}
                    disabled={mcBusy || !mcField || mcBeforeLoading}
                  >
                    {renderMcBeforeOptions()}
                  </select>
                  <p className="text-xs opacity-70 mt-1">
                    {!mcField
                      ? "Choose a field first."
                      : mcBeforeLoading
                        ? "Loading existing values…"
                        : mcBeforeOptions?.error
                          ? "Could not load existing values."
                          : `${mcBeforeOptions?.values?.length || 0} existing value(s)` +
                            (mcBeforeOptions?.hasNull ? ", NULL present" : "") +
                            (mcBeforeOptions?.hasBlank ? ", blanks present" : "")}
                  </p>
                </div>
                <div>
                  <label className="block mb-1 font-semibold">
                    3. After value (set to)
                  </label>
                  {renderMcAfterInput()}
                  <label className="inline-flex items-center gap-2 mt-1 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={mcAfterIsNull}
                      onChange={(e) => setMcAfterIsNull(e.target.checked)}
                      disabled={mcBusy || !mcField}
                    />
                    <span>Set field to NULL (clear value)</span>
                  </label>
                </div>
              </div>

              {/* Step 4 — Filters */}
              <div>
                <label className="block mb-1 font-semibold">
                  4. Filters (scope which leases are affected)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Client / Company
                    </span>
                    <input
                      className={getInputClasses(isDark)}
                      value={companyName || ""}
                      disabled
                    />
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Lease status
                    </span>
                    <select
                      value={mcFilterStatus}
                      onChange={(e) => setMcFilterStatus(e.target.value)}
                      className={getInputClasses(isDark)}
                      disabled={mcBusy}
                    >
                      <option value="">Any</option>
                      <option value="Y">Active only</option>
                      <option value="N">Inactive only</option>
                    </select>
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Current pumper
                    </span>
                    <select
                      value={mcFilterPumper}
                      onChange={(e) => setMcFilterPumper(e.target.value)}
                      className={getInputClasses(isDark)}
                      disabled={mcBusy}
                    >
                      <option value="">Any</option>
                      {validPumpers?.map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Current relief pumper
                    </span>
                    <select
                      value={mcFilterRelief}
                      onChange={(e) => setMcFilterRelief(e.target.value)}
                      className={getInputClasses(isDark)}
                      disabled={mcBusy}
                    >
                      <option value="">Any</option>
                      {validPumpers?.map((u) => (
                        <option key={u.UserID} value={u.UserID}>
                          {u.UserID} - {u.FullName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Group ID
                    </span>
                    <input
                      type="text"
                      className={getInputClasses(isDark)}
                      value={mcFilterGroup}
                      onChange={(e) => setMcFilterGroup(e.target.value)}
                      disabled={mcBusy}
                      placeholder="Exact Group ID"
                    />
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      District
                    </span>
                    <input
                      type="text"
                      className={getInputClasses(isDark)}
                      value={mcFilterDistrict}
                      onChange={(e) => setMcFilterDistrict(e.target.value)}
                      disabled={mcBusy}
                      placeholder="Exact District"
                    />
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Route
                    </span>
                    <input
                      type="text"
                      className={getInputClasses(isDark)}
                      value={mcFilterRoute}
                      onChange={(e) => setMcFilterRoute(e.target.value)}
                      disabled={mcBusy}
                      placeholder="Exact Route"
                    />
                  </div>
                  <div>
                    <span className="block mb-1 text-xs opacity-80">
                      Lease name contains
                    </span>
                    <input
                      type="text"
                      className={getInputClasses(isDark)}
                      value={mcFilterNameSearch}
                      onChange={(e) => setMcFilterNameSearch(e.target.value)}
                      disabled={mcBusy}
                      placeholder="Search lease name"
                    />
                  </div>
                </div>
              </div>

              {/* Step 5 — Preview */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-semibold">5. Preview</label>
                  <button
                    onClick={mcRunPreview}
                    className="px-3 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-70"
                    disabled={mcBusy}
                  >
                    {mcBusy ? "Working..." : "Run Preview"}
                  </button>
                </div>
                {mcPreview ? (
                  <div
                    className={`rounded border p-2 ${
                      isDark ? "border-gray-600" : "border-gray-300"
                    }`}
                  >
                    <p className="mb-1">
                      <strong>{mcPreview.matchCount}</strong> lease(s) match.
                      {mcPreview.matchCount > 0 && (
                        <>
                          {" "}
                          Field <strong>{mcFieldDef?.label || mcField}</strong>{" "}
                          will be set to{" "}
                          <strong>
                            {mcPreview.proposedIsNull
                              ? "NULL (empty)"
                              : String(mcPreview.proposedValue)}
                          </strong>
                          .
                        </>
                      )}
                    </p>
                    {mcPreview.matchCount > 0 && (
                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full text-xs">
                          <thead className={getHeaderClasses()}>
                            <tr>
                              <th className="py-1 px-2 text-left">LeaseID</th>
                              <th className="py-1 px-2 text-left">
                                Lease Name
                              </th>
                              <th className="py-1 px-2 text-left">
                                Current value
                              </th>
                              <th className="py-1 px-2 text-left">
                                Proposed value
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {mcPreview.leases?.map((l) => (
                              <tr key={l.LeaseID} className={getRowClasses()}>
                                <td className="py-1 px-2">{l.LeaseID}</td>
                                <td className="py-1 px-2">{l.LeaseName}</td>
                                <td className="py-1 px-2">
                                  {l.current === null || l.current === ""
                                    ? "(empty)"
                                    : String(l.current)}
                                </td>
                                <td className="py-1 px-2">
                                  {mcPreview.proposedIsNull
                                    ? "(empty)"
                                    : String(mcPreview.proposedValue)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs opacity-70">
                    Run a preview to see how many leases match and what will
                    change. Preview is read-only and does not modify the
                    database.
                  </p>
                )}
              </div>

              {/* Apply result summary */}
              {mcResult && (
                <div className="rounded border border-green-500 bg-green-500/10 p-2">
                  <p className="font-semibold text-green-600 dark:text-green-400">
                    Mass change applied.
                  </p>
                  <p>
                    Field <strong>{mcResult.field}</strong> updated on{" "}
                    <strong>{mcResult.affectedRows}</strong> lease(s)
                    {typeof mcResult.matchCount !== "undefined" && (
                      <> ({mcResult.matchCount} matched)</>
                    )}
                    .
                  </p>
                </div>
              )}

              {/* Step 6 — Confirm apply */}
              {mcPreview && mcPreview.matchCount > 0 && (
                <div className="rounded border border-amber-500 bg-amber-500/10 p-2">
                  <p className="text-sm">
                    <strong>Warning:</strong> applying will permanently update{" "}
                    {mcPreview.matchCount} lease(s). This cannot be undone.
                    Review the preview above before continuing.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={closeMassChange}
                className={`px-3 py-1 rounded ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
                disabled={mcBusy}
              >
                Close
              </button>
              <button
                onClick={mcApply}
                className="px-3 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
                disabled={mcBusy || !mcPreview || !mcPreview.matchCount}
                title={
                  !mcPreview
                    ? "Run a preview first"
                    : !mcPreview.matchCount
                      ? "No leases match"
                      : "Apply the mass change"
                }
              >
                {mcBusy ? "Applying..." : "Confirm & Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * SortableHeader is a reusable table-header cell for sorting.
 * It shows an up/down icon if the column is currently sorted.
 */
function SortableHeader({
  label,
  columnKey,
  sortBy,
  sortDirection,
  onSort,
  theme,
}) {
  const isSortedColumn = sortBy === columnKey;
  const isDark = theme === "dark";

  return (
    <th
      className="py-2 px-4 text-left cursor-pointer select-none"
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {isSortedColumn && (
          <FontAwesomeIcon
            icon={sortDirection === "asc" ? faSortUp : faSortDown}
            className={isDark ? "text-gray-200" : "text-gray-600"}
          />
        )}
      </div>
    </th>
  );
}

export default LeasesTab;
